import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const GenerateInvoiceModal = ({ tenantId, onClose, onGenerate }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedSubscription, setSelectedSubscription] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadActiveSubscriptions();
  }, [tenantId]);

  const loadActiveSubscriptions = async () => {
    try {
      const { data, error } = await supabase
        ?.from('member_subscriptions')
        ?.select(`
          *,
          user_profiles:member_id(id, full_name, email),
          membership_plans:plan_id(plan_name, plan_code, price)
        `)
        ?.eq('tenant_id', tenantId)
        ?.eq('status', 'active')
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      setSubscriptions(data || []);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedSubscription) {
      alert('Please select a subscription');
      return;
    }

    setGenerating(true);
    await onGenerate?.(selectedSubscription);
    setGenerating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
            <Icon name="FileText" size={24} className="text-primary" />
            Generate Invoice
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Close"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Icon name="Loader2" size={32} className="animate-spin text-primary" />
            </div>
          ) : subscriptions?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No active subscriptions found</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Select Subscription
                </label>
                <select
                  value={selectedSubscription}
                  onChange={(e) => setSelectedSubscription(e?.target?.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Choose a subscription...</option>
                  {subscriptions?.map((sub) => (
                    <option key={sub?.id} value={sub?.id}>
                      {sub?.user_profiles?.full_name} - {sub?.membership_plans?.plan_name} (${sub?.final_price || sub?.monthly_price}/month)
                    </option>
                  ))}
                </select>
              </div>

              {selectedSubscription && (
                <div className="bg-muted/50 border border-border rounded-lg p-4">
                  <h3 className="font-semibold text-foreground mb-3">Invoice Preview</h3>
                  {(() => {
                    const selected = subscriptions?.find(s => s?.id === selectedSubscription);
                    const amount = parseFloat(selected?.final_price || selected?.monthly_price || 0);
                    const taxRate = 0.08;
                    const taxAmount = amount * taxRate;
                    const totalAmount = amount + taxAmount;

                    return (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Member:</span>
                          <span className="text-foreground font-medium">{selected?.user_profiles?.full_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Plan:</span>
                          <span className="text-foreground font-medium">{selected?.membership_plans?.plan_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Billing Cycle:</span>
                          <span className="text-foreground font-medium">{selected?.billing_cycle || 'Monthly'}</span>
                        </div>
                        <div className="border-t border-border pt-2 mt-2">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Subtotal:</span>
                            <span className="text-foreground">${amount?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Tax (8%):</span>
                            <span className="text-foreground">${taxAmount?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between font-bold text-lg mt-2 pt-2 border-t border-border">
                            <span className="text-foreground">Total:</span>
                            <span className="text-primary">${totalAmount?.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-border flex justify-end gap-3">
          <Button
            onClick={onClose}
            variant="outline"
            disabled={generating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={!selectedSubscription || generating}
            iconName={generating ? 'Loader2' : 'FileText'}
            iconSize={20}
            className={generating ? 'animate-spin' : ''}
          >
            {generating ? 'Generating...' : 'Generate Invoice'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GenerateInvoiceModal;